import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FileSearch } from "lucide-react";
import { api } from "@/lib/api/client";
import { PageHeader } from "@/components/ui/PageHeader";
import { downloadFile } from "@/lib/api/download";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { Pagination, DEFAULT_PAGE_SIZE } from "@/components/ui/Pagination";
import { SearchPanel, filterInputClass } from "@/components/ui/SearchPanel";
import { Button } from "@/components/ui/button";

/**
 * Công bố văn bản — danh sách văn bản CHƯA công bố thuộc phạm vi đơn vị người
 * dùng, ngày đến trong 1 năm gần nhất (mặc định của màn hệ cũ,
 * DocumentPublishAction/actionSearchDocPublish). Cột lấy theo lưới hệ cũ.
 */
type PendingPublish = {
  DOCUMENT_ID: number;
  REGISTER_NUMBER: string | null;
  CODE: string | null;
  TITLE: string | null;
  CONTENT: string | null;
  PROMULGATE_DATE: string | null;
  RECEIVE_DATE: string | null;
  DEADLINE_DATE: string | null;
  SIGNER: string | null;
  SECURITY_NAME: string | null;
  TYPE_NAME: string | null;
};

type Filters = {
  code: string;
  promulgateFrom: string;
  promulgateTo: string;
  published: string;
  fromDate: string;
  toDate: string;
  typeId: string;
  deadlineFrom: string;
  deadlineTo: string;
  registerNumber: string;
  signer: string;
  areaId: string;
  title: string;
  content: string;
};
const EMPTY: Filters = {
  code: "",
  promulgateFrom: "",
  promulgateTo: "",
  published: "",
  fromDate: "",
  toDate: "",
  typeId: "",
  deadlineFrom: "",
  deadlineTo: "",
  registerNumber: "",
  signer: "",
  areaId: "",
  title: "",
  content: "",
};

async function fetchPendingPublish(page: number, pageSize: number, f: Filters) {
  const { data } = await api.get("/document/published-list", {
    params: {
      page,
      pageSize: pageSize,
      ...toParams(f),
    },
  });
  return (data.data ?? data) as { items: PendingPublish[]; total: number };
}

const day = (v: string | null) => (v ? v.slice(0, 10) : "—");

const toParams = (f: Filters) =>
  Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "")) as Record<string, string>;

export function DocumentPublishedPage() {
  const nav = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters>(EMPTY);

  const { data: types } = useQuery({
    queryKey: ["document-types"],
    queryFn: async () => {
      const { data } = await api.get("/document-type/list");
      return (data.data ?? data) as { typeId: number; typeName: string | null }[];
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ["document-pending-publish", page, pageSize, applied],
    queryFn: () => fetchPendingPublish(page, pageSize, applied),
  });

  const columns: Column<PendingPublish>[] = [
    {
      key: "stt",
      header: "STT",
      width: "56px",
      align: "center",
      render: (_r, i) => (
        <span className="text-muted-foreground tabular-nums">{(page - 1) * pageSize + i + 1}</span>
      ),
    },
    {
      key: "reg",
      header: "Số, ký hiệu văn bản",
      width: "160px",
      render: (r) => (
        <span className="font-mono text-xs">
          <span className="font-medium">{r.REGISTER_NUMBER ?? "—"}</span>
          <div className="text-muted-foreground text-xxs">{r.CODE ?? ""}</div>
        </span>
      ),
    },
    {
      key: "promulgate",
      header: "Ngày ban hành",
      width: "120px",
      render: (r) => <span className="text-sm tabular-nums">{day(r.PROMULGATE_DATE)}</span>,
    },
    {
      key: "receive",
      header: "Ngày đến",
      width: "110px",
      render: (r) => <span className="text-sm tabular-nums">{day(r.RECEIVE_DATE)}</span>,
    },
    {
      key: "deadline",
      header: "Hạn xử lý",
      width: "110px",
      render: (r) => <span className="text-sm tabular-nums">{day(r.DEADLINE_DATE)}</span>,
    },
    { key: "signer", header: "Người ký", width: "160px", render: (r) => r.SIGNER ?? "—" },
    { key: "security", header: "Độ mật", width: "110px", render: (r) => r.SECURITY_NAME ?? "—" },
    {
      key: "title",
      header: "Trích yếu nội dung",
      render: (r) => <span className="font-medium">{r.TITLE ?? "—"}</span>,
    },
    {
      key: "content",
      header: "Nội dung công bố văn bản",
      render: (r) => <span className="text-sm">{r.CONTENT ?? "—"}</span>,
    },
    { key: "type", header: "Hình thức văn bản", width: "150px", render: (r) => r.TYPE_NAME ?? "—" },
    {
      key: "view",
      header: "Xem",
      width: "70px",
      align: "center",
      render: (r) => (
        <Button
          size="sm"
          variant="outline"
          onClick={(e) => {
            e.stopPropagation();
            nav(`/documents/${r.DOCUMENT_ID}`);
          }}
        >
          Xem
        </Button>
      ),
    },
  ];

  const submit = () => {
    setApplied(filters);
    setPage(1);
  };
  const set = <K extends keyof Filters>(k: K, v: Filters[K]) =>
    setFilters((f) => ({ ...f, [k]: v }));
  const text = (label: string, key: keyof Filters, placeholder?: string) => ({
    label,
    control: (
      <input
        className={filterInputClass}
        placeholder={placeholder}
        value={filters[key]}
        onChange={(e) => set(key, e.target.value)}
      />
    ),
  });
  const date = (label: string, key: keyof Filters) => ({
    label,
    control: (
      <input
        type="date"
        className={filterInputClass}
        value={filters[key]}
        onChange={(e) => set(key, e.target.value)}
      />
    ),
  });

  /** Nút "Xuất báo cáo" — hệ cũ có ở mọi lưới báo cáo. */
  const onExport = () =>
    downloadFile("/document/published-list/export", toParams(applied), "cong-bo-van-ban.csv");

  return (
    <div>
      <PageHeader
        title="Công bố văn bản"
        subtitle={`${(data?.total ?? 0).toLocaleString("vi-VN")} văn bản chờ công bố`}
        breadcrumb="Quản lý Văn bản › Công bố văn bản"
      />
      <SearchPanel
        fields={[
          text("Số, ký hiệu văn bản", "code"),
          date("Ngày ban hành: từ ngày", "promulgateFrom"),
          date("đến", "promulgateTo"),
          {
            label: "Trạng thái",
            control: (
              <select
                className={filterInputClass}
                value={filters.published}
                onChange={(e) => set("published", e.target.value)}
              >
                <option value="">---- Tất cả ----</option>
                <option value="0">Chưa công bố</option>
                <option value="1">Đã công bố</option>
              </select>
            ),
          },
          date("Ngày đến: từ ngày", "fromDate"),
          date("đến", "toDate"),
          {
            label: "Hình thức văn bản",
            control: (
              <select
                className={filterInputClass}
                value={filters.typeId}
                onChange={(e) => set("typeId", e.target.value)}
              >
                <option value="">---- Tất cả ----</option>
                {(types ?? []).map((t) => (
                  <option key={t.typeId} value={t.typeId}>
                    {t.typeName ?? t.typeId}
                  </option>
                ))}
              </select>
            ),
          },
          date("Hạn cuối: từ ngày", "deadlineFrom"),
          date("đến", "deadlineTo"),
          text("Số đăng ký", "registerNumber"),
          text("Người ký", "signer"),
          text("Tên Lĩnh vực", "areaId", "ID lĩnh vực"),
          text("Trích yếu nội dung", "title"),
          text("Nội dung công bố văn bản", "content"),
        ]}
        onSubmit={submit}
        onExport={onExport}
      />
      <DataTable<PendingPublish>
        columns={columns}
        rows={data?.items ?? []}
        keyBy={(r, i) => `${r.DOCUMENT_ID}-${i}`}
        isLoading={isLoading}
        onRowClick={(r) => nav(`/documents/${r.DOCUMENT_ID}`)}
        empty={
          <>
            <FileSearch className="inline h-4 w-4 mr-1" /> Không có văn bản chờ công bố
          </>
        }
        dense
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
      />
    </div>
  );
}
